import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { track } from "../utils/rudderstack";
import { NoSitesReason, isValidNoSitesReason } from "../types/eligibility";
import { usePartnerConfig, mergePartnerConfig } from "../hooks/usePartnerConfig";

// Message content mapping
const messageContent = {
  [NoSitesReason.GEOGRAPHIC]: {
    message:
      "We don't currently have trial sites for this study near your location.",
    subtext:
      "We're redirecting you to HealthMatch where you can find clinical trials that are available in your area.",
  },
  [NoSitesReason.DISQUALIFIED]: {
    message:
      "Based on your responses, we've identified other trials that might be a better fit for you.",
    subtext:
      "We're redirecting you to HealthMatch where you can explore a wider range of clinical trials suited to your profile.",
  },
  [NoSitesReason.AGE_INELIGIBLE]: {
    message:
      "Based on your age, you don't meet the eligibility requirements for this study.",
    subtext:
      "We're redirecting you to HealthMatch where you can find clinical trials that are suitable for your age group.",
  },
};

export function NoSitesAvailablePageContent({
  config,
}: {
  config: LandingPageConfig;
}) {
  const [redirectCountdown, setRedirectCountdown] = useState(7);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [searchParams] = useSearchParams();
  const partnerConfig = usePartnerConfig();

  const reasonParam = searchParams.get("reason") || "";
  const reason = isValidNoSitesReason(reasonParam)
    ? reasonParam
    : NoSitesReason.GEOGRAPHIC;

  // Get the appropriate content based on the reason
  const content =
    messageContent[reason] || messageContent[NoSitesReason.GEOGRAPHIC];

  const redirectConditionId = config.redirectConditionId ?? config.conditionId;
  const redirectUrl = `https://app.healthmatch.io/questionnaire/condition/${redirectConditionId}`;

  // Track page view and handle redirect
  useEffect(() => {
    // Track page view
    track("lp.no_sites_page.viewed", {
      landing_page_id: config.id,
      reason: reason || "unknown",
    });

    // Setup countdown for redirect
    const interval = setInterval(() => {
      setRedirectCountdown((count) => {
        if (count <= 1 && !hasRedirected) {
          clearInterval(interval);
          handleRedirect();
          return 0;
        }
        return count - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle the actual redirect
  const handleRedirect = () => {
    if (hasRedirected) return;
    setHasRedirected(true);
    track("lp.no_sites_page.redirected", {
      landing_page_id: config.id,
      destination: "healthmatch_platform",
      reason: reason || "unknown",
    });
    window.location.href = redirectUrl;
  };

  // Track manual redirect click
  const handleManualRedirect = () => {
    track("lp.no_sites_page.manual_redirect_clicked", {
      landing_page_id: config.id,
      reason: reason || "unknown",
    });
  };

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="no-sites-page">
        {/* Background orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>
          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>
        </div>

        {/* Main content */}
        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-16 pb-12 md:pb-24"
          data-testid="no-sites-content"
        >
          <div className="w-full max-w-4xl mx-auto px-2">
            <div
              className="bg-white shadow-xl rounded-2xl overflow-hidden p-8 flex flex-col items-center justify-center text-center"
              data-testid="no-sites-card"
            >
              <p
                className="text-lg text-gray-700 mb-4"
                data-testid="no-sites-message"
              >
                {content.message}
              </p>
              <p
                className="text-md text-gray-600 mb-6"
                data-testid="no-sites-subtext"
              >
                {content.subtext}
              </p>
              <div className="mb-6">
                <div
                  className="animate-spin rounded-full h-20 w-20 border-b-4 border-amber-600"
                  data-testid="no-sites-spinner"
                ></div>
              </div>
              <p
                className="text-sm text-gray-500 mb-6"
                data-testid="no-sites-countdown"
              >
                Redirecting in {redirectCountdown}{" "}
                {redirectCountdown === 1 ? "second" : "seconds"}...
              </p>

              <div className="mt-2">
                <p className="text-sm text-gray-600 mb-2">
                  Not being redirected?
                </p>
                <a
                  href={redirectUrl}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  onClick={handleManualRedirect}
                  data-testid="no-sites-manual-link"
                >
                  Go to HealthMatch
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
